import { Component, Inject, OnDestroy, OnInit, computed } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { select, Store } from '@ngxs/store';
import { Subscription, tap } from 'rxjs';
import { markControlsAsTouched } from 'src/app/shared/functions/mark-controls-as-touched';
import { mapDepartmentInfosToSelectOptions } from 'src/app/shared/user-view/functions/enrich-select-options-with-department-pause';
import { TenantUserAccessState } from 'src/app/tenant-selector/state/tenant-user-access.state';
import { ChemicalExposureForm } from '../../forms/chemical-exposure.form';
import { createChemicalExposureForm } from '../../functions/create-chemical-exposure-form';
import { mapChemicalExposureRequest } from '../../functions/map-chemical-exposure-request';
import { ITransferProductRequest } from '../../models/product.model';
import { LoadMatchingProducts } from '../../state/product.actions';
import { ProductState } from '../../state/product.state';

export interface TransferProductModalData {
  productId: string;
  sourceDepartmentId: string;
  productName: string;
  maxQuantity: number;
  isChemical?: boolean;
  manufacturerName?: string;
}

@Component({
  selector: 'app-transfer-product-modal',
  templateUrl: './transfer-product-modal.component.html',
  styleUrls: ['./transfer-product-modal.component.scss'],
  standalone: false,
})
export class TransferProductModalComponent implements OnInit, OnDestroy {
  departmentControl = new FormControl<string | null>(null, [Validators.required]);
  targetProductControl = new FormControl('');
  quantityControl = new FormControl<number | null>(1);
  reasonControl = new FormControl('');
  chemicalExposureForm: ChemicalExposureForm | null = null;

  availableDepartmentsFromStore = select(TenantUserAccessState.getAllAvailableDepartments);
  availableDepartments = computed(() =>
    mapDepartmentInfosToSelectOptions(
      (this.availableDepartmentsFromStore() ?? []).filter(
        (department) => department.id !== this.data.sourceDepartmentId
      )
    )
  );

  matchingProducts = select(ProductState.getMatchingProducts);
  targetProducts = computed(() =>
    (this.matchingProducts() ?? []).map((product) => ({
      id: product.id,
      name: `${product.name}${product.articleNo ? ` (${product.articleNo})` : ''}, ${
        product.quantity
      } ${product.quantityType.name} - ${product.menu.name}`,
    }))
  );

  private subscription = new Subscription();

  constructor(
    private store: Store,
    private dialogRef: MatDialogRef<TransferProductModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: TransferProductModalData
  ) {
    if (this.data?.maxQuantity) {
      this.quantityControl.setValidators([
        Validators.required,
        Validators.min(1),
        Validators.max(this.data.maxQuantity),
      ]);
    }
    if (this.data?.isChemical) {
      this.chemicalExposureForm = createChemicalExposureForm({
        name: this.data.productName,
        maker: this.data.manufacturerName,
      });
    }
  }

  get isFormValid(): boolean {
    if (!this.departmentControl.value || !this.quantityControl.value) {
      return false;
    }
    if (this.chemicalExposureForm && !this.chemicalExposureForm.valid) {
      return false;
    }
    return true;
  }

  ngOnInit(): void {
    this.subscription.add(
      this.departmentControl.valueChanges
        .pipe(
          tap((targetDepartmentId: string | null) => {
            if (targetDepartmentId) {
              this.store.dispatch(
                new LoadMatchingProducts(
                  this.data.productId,
                  targetDepartmentId,
                  this.data.sourceDepartmentId
                )
              );
            }
          })
        )
        .subscribe()
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  onTransfer(): void {
    if (this.chemicalExposureForm) {
      markControlsAsTouched(this.chemicalExposureForm);
      this.chemicalExposureForm.updateValueAndValidity();
    }
    if (!this.isFormValid) {
      return;
    }

    const transferRequest: ITransferProductRequest = {
      sourceStorageProductId: this.data.productId,
      targetDepartmentId: this.departmentControl.value || '',
      quantity: this.quantityControl.value || 0,
      targetStorageProductId: this.targetProductControl.value || '',
      reasonForTransfer: this.reasonControl.value || '',
    };
    if (this.chemicalExposureForm) {
      transferRequest.chemicalExposure = mapChemicalExposureRequest(
        this.chemicalExposureForm.getRawValue()
      );
    }
    this.dialogRef.close(transferRequest);
  }
}
